import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Box,
  Select,
  MenuItem,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../../../firebase-config";
import { doc, getDoc, updateDoc } from "firebase/firestore";

const AdminProjectsDetails = () => {
  const { projectid } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const projectRef = doc(db, "projects", projectid);
        const projectSnap = await getDoc(projectRef);
        if (projectSnap.exists()) {
          setProject(projectSnap.data());
        } else {
          setError("Project not found.");
        }
      } catch (err) {
        console.error("Error fetching project:", err);
        setError("Failed to load project.");
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [projectid]);

  const handleUpdateProject = async (field, value) => {
    if (!project) return;

    try {
      setIsUpdating(true);
      const projectRef = doc(db, "projects", projectid);
      await updateDoc(projectRef, { [field]: value });
      setProject((prev) => ({ ...prev, [field]: value }));
    } catch (error) {
      console.error("Error updating project:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleApprove = async () => {
    await handleUpdateProject("status", "approved");
    alert("Project approved!");
    navigate("/admin/projects");
  };

  const handleReject = async () => {
    await handleUpdateProject("status", "rejected");
    alert("Project rejected.");
    navigate("/admin/projects");
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!project) return <Typography>No project found.</Typography>;

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Admin Review: {project.title}
      </Typography>

      <TextField
        label="Title"
        fullWidth
        value={project.title}
        onChange={(e) => handleUpdateProject("title", e.target.value)}
        sx={{ mb: 2 }}
      />

      <TextField
        label="Description"
        fullWidth
        multiline
        rows={3}
        value={project.description}
        onChange={(e) => handleUpdateProject("description", e.target.value)}
        sx={{ mb: 2 }}
      />

      <TextField
        label="Budget ($)"
        fullWidth
        type="number"
        value={project.budget}
        onChange={(e) => handleUpdateProject("budget", e.target.value)}
        sx={{ mb: 2 }}
      />

      <TextField
        label="Requirements"
        fullWidth
        multiline
        rows={2}
        value={project.requirements.join(", ")}
        onChange={(e) =>
          handleUpdateProject("requirements", e.target.value.split(",").map((req) => req.trim()))
        }
        sx={{ mb: 2 }}
      />

      {/* Project Status */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6">Project Status</Typography>
        <Select
          fullWidth
          value={project.status}
          onChange={(e) => handleUpdateProject("status", e.target.value)}
          sx={{ mb: 2 }}
        >
          <MenuItem value="draft">Draft</MenuItem>
          <MenuItem value="submitted">Submitted</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
        </Select>
      </Box>

      {/* Admin Notes */}
      <TextField
        label="Admin Notes"
        fullWidth
        multiline
        rows={3}
        value={project.adminNotes || ""}
        onChange={(e) => handleUpdateProject("adminNotes", e.target.value)}
        sx={{ mb: 2 }}
      />

      {/* Approve/Reject Buttons */}
      <Box mt={3}>
        <Button
          variant="contained"
          color="success"
          onClick={handleApprove}
          disabled={isUpdating}
          sx={{ mr: 2 }}
        >
          Approve
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleReject}
          disabled={isUpdating}
        >
          Reject
        </Button>
      </Box>
    </Container>
  );
};

export default AdminProjectsDetails;
